/**
 * TiledMorphingHemisphere - Hemisphere with dynamically loaded map tiles.
 *
 * Combines the MorphingHemisphere base with TileMesh overlays for
 * map imagery that deforms with the curvature.
 */

import { Suspense } from 'react'
import { MorphingHemisphere } from './MorphingHemisphere'
import { TileMesh, TileMeshFallback } from './TileMesh'
import { useTileLoader } from '@/domain/globe/hooks/useTileLoader'
import { useGlobeRenderer } from '@/domain/globe/hooks/useGlobeRenderer'
import { getTileKey, tileToLatLngBounds, applyRenderOffset } from '@/domain/globe/utils/tileCalculations'
import { GLOBE_CONFIG } from '@/domain/globe/constants'
import type { GlobeTile, VisibleTile } from '@/domain/globe/types'

interface TiledMorphingHemisphereProps {
  /** Whether to show wireframe for debugging */
  wireframe?: boolean
  /** Whether to show the base hemisphere (can be hidden when tiles fully cover) */
  showBase?: boolean
  /** Enable debug visualization (axes helper, coordinate markers) */
  debug?: boolean
  /** Show tile debug overlay (boundary lines + z/x/y labels) */
  showTileDebugOverlay?: boolean
}

/**
 * TiledMorphingHemisphere component.
 *
 * Renders a hemisphere with map tiles overlaid. The tiles deform
 * with the hemisphere as curvature changes.
 *
 * Must be used within a GlobeMapProvider context.
 *
 * @example
 * ```tsx
 * <GlobeMapProvider>
 *   <Canvas>
 *     <TiledMorphingHemisphere />
 *   </Canvas>
 * </GlobeMapProvider>
 * ```
 */
/**
 * Helper to get bounds for a tile coordinate, applying renderOffset for dateline wrapping.
 */
function getBoundsForCoord(coord: VisibleTile): { north: number; south: number; east: number; west: number } {
  const baseBounds = tileToLatLngBounds(coord)
  return applyRenderOffset(baseBounds, coord.renderOffset)
}

export function TiledMorphingHemisphere({
  wireframe = false,
  showBase = true,
  debug = false,
  showTileDebugOverlay = false,
}: TiledMorphingHemisphereProps) {
  const { curvature } = useGlobeRenderer()
  const { tilesWithFallbacks } = useTileLoader()

  return (
    <group name="tiled-morphing-hemisphere">
      {/* Debug: Axes helper (X=red, Y=green, Z=blue) */}
      {debug && <axesHelper args={[GLOBE_CONFIG.SPHERE_RADIUS * 1.5]} />}

      {/* Base hemisphere (dark background, visible through gaps) */}
      {showBase && (
        <MorphingHemisphere
          curvature={curvature}
          color={0x1a1a2e}
          wireframe={wireframe || debug}
        />
      )}

      {/* Map tiles - either loaded tiles or fallback ancestors */}
      {tilesWithFallbacks.map(({ coord, tile, fallback }) => {
        const renderBounds = getBoundsForCoord(coord)

        // Case 1: Tile is loaded - render it directly
        if (tile) {
          const enrichedTile: GlobeTile = {
            ...tile,
            bounds: renderBounds,
          }
          return (
            <Suspense
              key={getTileKey(coord)}
              fallback={
                <TileMeshFallback
                  tile={enrichedTile}
                  curvature={curvature}
                  radius={GLOBE_CONFIG.SPHERE_RADIUS}
                  showDebugOverlay={showTileDebugOverlay}
                />
              }
            >
              <TileMesh
                tile={enrichedTile}
                curvature={curvature}
                radius={GLOBE_CONFIG.SPHERE_RADIUS}
                showDebugOverlay={showTileDebugOverlay}
              />
            </Suspense>
          )
        }

        // Case 2: Have a fallback ancestor - render it with UV transform
        if (fallback) {
          return (
            <Suspense
              key={`fallback-${getTileKey(coord)}`}
              fallback={
                <TileMeshFallback
                  tile={{ coord, bounds: renderBounds, status: 'loading', url: '' }}
                  curvature={curvature}
                  radius={GLOBE_CONFIG.SPHERE_RADIUS}
                  showDebugOverlay={showTileDebugOverlay}
                />
              }
            >
              <TileMesh
                tile={fallback.tile}
                curvature={curvature}
                radius={GLOBE_CONFIG.SPHERE_RADIUS}
                showDebugOverlay={showTileDebugOverlay}
                uvOffset={fallback.uvOffset}
                uvScale={fallback.uvScale}
                renderBounds={renderBounds}
              />
            </Suspense>
          )
        }

        // Case 3: No tile and no fallback - show loading placeholder
        const placeholderTile: GlobeTile = {
          coord,
          bounds: renderBounds,
          status: 'loading',
          url: '',
          renderOffset: coord.renderOffset,
        }
        return (
          <TileMeshFallback
            key={`loading-${getTileKey(coord)}`}
            tile={placeholderTile}
            curvature={curvature}
            radius={GLOBE_CONFIG.SPHERE_RADIUS}
            showDebugOverlay={showTileDebugOverlay}
          />
        )
      })}
    </group>
  )
}

/**
 * Simple version without tile loading (for testing/development).
 */
export function SimpleMorphingHemisphere({
  curvature = 1,
  wireframe = false,
}: {
  curvature?: number
  wireframe?: boolean
}) {
  return (
    <MorphingHemisphere
      curvature={curvature}
      color={0x1a1a2e}
      wireframe={wireframe}
    />
  )
}
